// src/pages/Auth/ForgetPassword.jsx

import { Box } from '@mui/material';
import CustomText, { textStyles } from "../../../components/CustomText";
import CustomButton from "../../../components/CustomButton";
import BaseCard from '../../../components/Cards/Card';
import CustomEmailInput from '../../../components/EmailInput';
import { useTheme } from "@mui/material/styles";
import useMediaQuery from '@mui/material/useMediaQuery';
import { useFormik } from 'formik';
import { toast, Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import { BASE_URL } from '../../../components/BaseUrl';
import { useState } from 'react';
import { setOtpData } from '../../../Redux/Slices/AuthSlice';
import { useDispatch } from 'react-redux';
import { apiFetch } from '../../../utils/apiService';

const ForgetPassword = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const validate = (values) => {
    const errors = {};
    if (!values.email) {
      errors.email = t('forgetPassword.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      errors.email = t('forgetPassword.emailInvalid');
    }
    return errors;
  };

  const handleSubmit = async (values) => {
    const errors = validate(values);
    if (Object.keys(errors).length > 0) {
      if (errors.email) toast.error(errors.email);
      return;
    }

    setLoading(true);

    try {
      const data = await apiFetch(`${BASE_URL}/company-admins/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: values.email }),
      });

      console.groupCollapsed("📩 Forget Password API");
      console.log("Full response:", data);
      console.log("DebugCode received:", data?.data?.debugCode);
      console.groupEnd();

      if (!data.ok || data.error) {
        throw new Error(data.message || 'Something went wrong');
      }

      toast.success(data.message);

      // ✅ Save email + debugCode into Redux
      dispatch(setOtpData({
        email: values.email,
        debugCode: data?.data?.debugCode || null,
      }));

      // 👀 Optional: alert devs with debugCode
      // alert(`DebugCode for testing: ${data?.data?.debugCode}`);

      // Optional redirect
      setTimeout(() => {
        navigate(`/verification`);
      }, 1000);

    } catch (error) {
      toast.error(error.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: { email: '' },
    validate,
    onSubmit: handleSubmit,
  });

  return (
    <Box sx={{
      backgroundColor: "#EFF4F8",
      minHeight: "100vh",
      py: isMobile ? 5 : 10,
      px: 2,
      display: 'flex',
      justifyContent: 'center',
      alignItems: "center"
    }}>
     
      <BaseCard
        maxWidth={isMobile ? '100%' : 380}
        sx={{
          width: isMobile ? '100%' : 'auto',
          boxSizing: 'border-box',
          px: isMobile ? 2 : 0,
        }}
      >
        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
              <CustomText sx={{ ...textStyles.h4, color: theme.palette.tertiary.main }}>
                {t('forgetPassword.title')}
              </CustomText>
              <CustomText customStyle={{ ...textStyles.body4, color: theme.palette.text.primary }}>
                {t('forgetPassword.subtitle')}
              </CustomText>
            </Box>

            <label className='email_label'>{t('forgetPassword.emailLabel')}</label>
            <CustomEmailInput
              name="email"
              placeholder='john@gmail.com'
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && formik.errors.email}
            />
            {formik.touched.email && formik.errors.email && (
              <span style={{ color: 'red', fontSize: '12px' }}>{formik.errors.email}</span>
            )}

            <CustomButton
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {loading && <CircularProgress size={18} sx={{ color: '#fff', mr: 1 }} />}
                  {t('forgetPassword.submitButton')}
                </Box>
              }
              type="submit"
              fullWidth
              sx={{ mt: 2 }}
            />
          </Box>
        </form>
      </BaseCard>
    </Box>
  );
};

export default ForgetPassword;






